import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Key,
  Copy,
  Check,
  AlertTriangle,
  Shield,
  Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import { getCurrentEntity } from "../lib/api";
import { toast } from "sonner";

export default function KeyGenerationPage() {
  const { t } = useTranslation();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadEntity();
  }, []);

  const loadEntity = async () => {
    try {
      const response = await getCurrentEntity();
      setEntity(response.data);
    } catch (error) {
      toast.error(t('errors.loadingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPublicKey = () => {
    if (entity?.public_key) {
      navigator.clipboard.writeText(entity.public_key);
      setCopied(true);
      toast.success(t('keyGeneration.publicKeyCopied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="content-container max-w-3xl" data-testid="keys-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold text-slate-900">
            Gestió de Claus
          </h1>
          <p className="text-slate-500 mt-1">
            Informació sobre les claus criptogràfiques de la teva entitat
          </p>
        </motion.div>

        {/* Warning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900 mb-2">{t('keyGeneration.privateKeyImportant')}</h3>
              <p className="text-sm text-amber-700">
                {t('keyGeneration.privateKeyWarning')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Key Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* How it works */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-heading font-semibold text-slate-900">
                {t('keyGeneration.howItWorks')}
              </h2>
            </div>
            <div className="space-y-4 text-sm text-slate-600">
              <p>
                {t('keyGeneration.rsaExplanation')}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>{t('keyGeneration.publicKeyLabel')}:</strong> {t('keyGeneration.publicKeyDesc')}
                </li>
                <li>
                  <strong>{t('keyGeneration.privateKeyLabel')}:</strong> {t('keyGeneration.privateKeyDividedDesc')}
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>{t('keyGeneration.part1Desc')}</li>
                    <li>{t('keyGeneration.part2Desc')}</li>
                  </ul>
                </li>
                <li>
                  {t('keyGeneration.bothPartsNeeded')}
                </li>
              </ul>
            </div>
          </div>

          {/* Public Key */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Key className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-900">
                    {t('keyGeneration.publicKeyTitle')}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {t('keyGeneration.usedToEncrypt')}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleCopyPublicKey}
                data-testid="copy-public-key-btn"
              >
                {copied ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            </div>
            <div className="key-display max-h-48 overflow-y-auto">
              {entity?.public_key}
            </div>
          </div>

          {/* Private Key Status */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <Shield className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-semibold text-slate-900">
                  {t('keyGeneration.privateKeyTitle')}
                </h2>
                <p className="text-sm text-slate-500">
                  {t('keyGeneration.dividedForSecurity')}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">
                  {t('keyGeneration.yourPart')}
                </span>
                <span className="text-sm text-slate-500">
                  {t('keyGeneration.savedAtRegistration')}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">
                  {t('keyGeneration.adminPartLabel')}
                </span>
                <span className="text-sm text-emerald-600">
                  ✓ {t('keyGeneration.savedInSystem')}
                </span>
              </div>
            </div>
          </div>

          {/* Entity Info */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
              {t('keyGeneration.entityInfo')}
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-500">{t('common.name')}</span>
                <span className="font-medium text-slate-900">{entity?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t('common.email')}</span>
                <span className="font-medium text-slate-900">{entity?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t('keyGeneration.registrationDate')}</span>
                <span className="font-medium text-slate-900">
                  {entity?.created_at 
                    ? new Date(entity.created_at).toLocaleDateString("ca-ES")
                    : t('common.notAvailable')}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
